import { MessageInstance } from 'antd/lib/message/interface';
import { useTranslation } from 'translations/hooks';
import { getLocale } from 'translations/locale';

import { useRestApiHeaders } from 'business/contextProviders/useApiHeaders';
import { PlotterMode } from 'business/data-analysis/constants';
import type { useFilters } from 'business/data-analysis/pages/graph/components/filter-card/hooks/use-filters';
import { GraphPageState } from 'business/data-analysis/pages/graph/hooks/graph-context/types';
import { mapToStructureDtoFromGraphSetStructure } from 'business/data-analysis/services/map-to-structure-dto-from-graph-set-structure';
import config from 'config';
import {
  useGraphSetExportControllerGenerateGraphSetPeriodExport,
  useGraphSetExportControllerGenerateGraphSetRingExport,
} from 'generated/apiComponents';
import { Module_Enum } from 'generated/graphql';
import { downloadFile } from 'technical/print';
import { notEmpty } from 'technical/utils/not-empty';

export const useExportGraphSet = (messageApi: MessageInstance) => {
  const { t } = useTranslation();

  const headers = useRestApiHeaders(Module_Enum.DataAnalysis);

  const {
    mutate: generateGraphSetPeriodExport,
    isPending: isPeriodExportPending,
  } = useGraphSetExportControllerGenerateGraphSetPeriodExport({
    onSuccess: (data) => {
      downloadFile(`${config.gcp.publicUri}/${data.url}`, data.fileName);
    },
    onError: () => {
      messageApi.open({
        type: 'error',
        content: t('errors.export_download_error'),
      });
    },
  });

  const { mutate: generateGraphSetRingExport, isPending: isRingExportPending } =
    useGraphSetExportControllerGenerateGraphSetRingExport({
      onSuccess: (data) => {
        downloadFile(`${config.gcp.publicUri}/${data.url}`, data.fileName);
      },
      onError: () => {
        messageApi.open({
          type: 'error',
          content: t('errors.export_download_error'),
        });
      },
    });

  const generateGraphSetExportByMode = (
    mode: PlotterMode,
    constructionSiteId: string,
    graphSetStructure: GraphPageState['structure'],
    filters: ReturnType<typeof useFilters>['filters'],
  ) => {
    if (mode === PlotterMode.TIME_PERIOD) {
      generateGraphSetPeriodExport({
        body: {
          constructionSiteId,
          startDate: filters.dateFilters.startDate.toISOString(),
          endDate: filters.dateFilters.endDate.toISOString(),
          graphSetStructure:
            mapToStructureDtoFromGraphSetStructure(graphSetStructure),
          locale: getLocale(),
        },
        headers,
      });
    }

    if (
      mode === PlotterMode.RING &&
      filters.ringFilters &&
      notEmpty(filters.ringFilters[0]) &&
      notEmpty(filters.ringFilters[1])
    ) {
      generateGraphSetRingExport({
        body: {
          constructionSiteId,
          startRing: filters.ringFilters[0],
          endRing: filters.ringFilters[1],
          graphSetStructure:
            mapToStructureDtoFromGraphSetStructure(graphSetStructure),
          locale: getLocale(),
        },
        headers,
      });
    }
  };

  return {
    generateGraphSetExportByMode,
    isGraphSetExportPending: isPeriodExportPending || isRingExportPending,
  };
};
